import { KeyboardEvent, useMemo } from "react";
import { IRutCandidate, parseRutList } from "../../../utils/rutList";

/**
 * Filtro de RUT reutilizable que replica el comportamiento del filtro de RUT de
 * la tabla de Postulaciones: se puede pegar una lista de RUTs (uno por línea, o
 * separados por coma/punto y coma), se validan en vivo mostrando un chip
 * verde/rojo por cada uno, y al presionar "Agregar RUTs válidos" (o Ctrl/Cmd +
 * Enter) se entregan al contenedor SOLO los RUTs válidos, ya formateados y sin
 * duplicados. Los RUTs inválidos quedan en el textarea para que el usuario los
 * corrija.
 *
 * El componente es controlado: el contenedor mantiene el texto en `value` y
 * recibe cambios por `onChange`. Al aplicar, el propio componente limpia el
 * textarea dejando solo las entradas inválidas (vía `onChange`) y llama a
 * `onApply` con los RUTs válidos.
 */
export interface RutListFilterProps {
  /** Texto actual del textarea (componente controlado). */
  value: string;
  /** Se llama cuando cambia el texto del textarea. */
  onChange: (value: string) => void;
  /**
   * Se llama al aplicar, con los RUTs válidos formateados (XXXXXXXX-X) y sin
   * duplicados. El contenedor decide cómo integrarlos a su filtro.
   */
  onApply: (validRuts: string[]) => void;
  placeholder?: string;
  buttonLabel?: string;
  /** Deshabilita el textarea y el botón. */
  disabled?: boolean;
  /** Estilo extra opcional para el contenedor. */
  style?: React.CSSProperties;
  /**
   * RUTs ya aplicados al filtro. Cuando se entrega junto con `onRemove`, el
   * componente los muestra como chips removibles debajo del botón. Se deja
   * opcional porque algunos contenedores (p. ej. `InputFiltro`) ya pintan sus
   * propios chips de filtros aplicados.
   */
  appliedValues?: string[];
  /** Se llama al quitar un RUT ya aplicado (requiere `appliedValues`). */
  onRemove?: (value: string) => void;
  /** Si se entrega, muestra un botón "Quitar todos" que lo invoca. */
  onClearAll?: () => void;
}

const RutListFilter = ({
  value,
  onChange,
  onApply,
  placeholder = "Pega uno o varios RUTs, uno por línea",
  buttonLabel = "Agregar RUTs válidos",
  disabled = false,
  style,
  appliedValues,
  onRemove,
  onClearAll,
}: RutListFilterProps) => {
  const rutCandidates: IRutCandidate[] = useMemo(() => parseRutList(value), [value]);
  const hasValid = rutCandidates.some((candidate) => candidate.isValid);

  const applyRutFilters = () => {
    if (disabled || rutCandidates.length === 0) return;

    const validRuts = rutCandidates
      .filter((candidate) => candidate.isValid)
      .map((candidate) => candidate.formatted);
    const invalidEntries = rutCandidates
      .filter((candidate) => !candidate.isValid)
      .map((candidate) => candidate.raw.trim())
      .filter(Boolean);

    // Los inválidos quedan en el textarea para que se puedan corregir.
    onChange(invalidEntries.join("\n"));
    if (validRuts.length === 0) return;

    onApply(Array.from(new Set(validRuts)));
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key === "Enter" && (event.ctrlKey || event.metaKey)) {
      event.preventDefault();
      applyRutFilters();
    }
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "7px", ...style }}>
      <textarea
        style={{
          width: "100%",
          minHeight: "92px",
          resize: "vertical",
          lineHeight: 1.4,
          padding: "8px 10px",
          border: "1px solid #cbd5e1",
          borderRadius: "8px",
          fontFamily: "Roboto, sans-serif",
          fontSize: "13px",
          color: "#334155",
          outline: "none",
          boxSizing: "border-box",
        }}
        placeholder={placeholder}
        value={value}
        disabled={disabled}
        onChange={(e) => onChange(e.target.value)}
        onKeyDown={handleKeyDown}
      />
      <button
        type="button"
        onClick={applyRutFilters}
        disabled={disabled || !hasValid}
        style={{
          alignSelf: "flex-start",
          padding: "6px 10px",
          borderRadius: "7px",
          border: "1px solid #0065DD",
          background: !disabled && hasValid ? "#0065DD" : "#e2e8f0",
          color: !disabled && hasValid ? "#fff" : "#94a3b8",
          cursor: !disabled && hasValid ? "pointer" : "not-allowed",
          fontFamily: "Roboto, sans-serif",
          fontSize: "11.5px",
          fontWeight: 600,
        }}
      >
        {buttonLabel}
      </button>

      {rutCandidates.length > 0 && (
        <div style={{ display: "flex", flexWrap: "wrap", gap: "5px", marginTop: "1px" }}>
          {rutCandidates.map((candidate, index) => (
            <span
              key={`${candidate.raw}-${index}`}
              title={candidate.error ?? "RUT válido"}
              style={{
                display: "inline-flex",
                alignItems: "center",
                padding: "3px 9px",
                borderRadius: "14px",
                background: candidate.isValid ? "#dcfce7" : "#fee2e2",
                border: `1px solid ${candidate.isValid ? "#86efac" : "#fca5a5"}`,
                color: candidate.isValid ? "#166534" : "#b91c1c",
                fontFamily: "ui-monospace, 'SF Mono', Menlo, Consolas, monospace",
                fontSize: "11px",
                fontWeight: 600,
              }}
            >
              {candidate.formatted || candidate.raw.toUpperCase()}
            </span>
          ))}
        </div>
      )}

      {appliedValues && onRemove && appliedValues.length > 0 && (
        <div
          style={{
            marginTop: "3px",
            border: "1px solid #e2e8f0",
            borderRadius: "8px",
            background: "#f8fafc",
            overflow: "hidden",
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: "8px",
              padding: "5px 9px",
              borderBottom: "1px solid #e2e8f0",
              background: "#f1f5f9",
            }}
          >
            <span
              style={{
                fontFamily: "Roboto, sans-serif",
                fontSize: "11px",
                fontWeight: 600,
                color: "#475569",
              }}
            >
              {appliedValues.length} RUT{appliedValues.length !== 1 ? "s" : ""} agregado
              {appliedValues.length !== 1 ? "s" : ""}
            </span>
            {onClearAll && (
              <button
                type="button"
                onClick={onClearAll}
                style={{
                  background: "none",
                  border: "none",
                  cursor: "pointer",
                  padding: 0,
                  color: "#0065DD",
                  fontFamily: "Roboto, sans-serif",
                  fontSize: "11px",
                  fontWeight: 600,
                }}
              >
                Quitar todos
              </button>
            )}
          </div>
          <div
            style={{
              display: "flex",
              flexWrap: "wrap",
              alignContent: "flex-start",
              gap: "5px",
              padding: "7px 9px",
              maxHeight: "108px",
              overflowY: "auto",
            }}
          >
            {appliedValues.map((val) => (
              <span
                key={val}
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: "4px",
                  padding: "2px 6px 2px 9px",
                  borderRadius: "12px",
                  background: "#e0f2fe",
                  border: "1px solid #bae6fd",
                  color: "#0369a1",
                  fontSize: "11px",
                  fontFamily: "ui-monospace, 'SF Mono', Menlo, Consolas, monospace",
                  fontWeight: 600,
                  whiteSpace: "nowrap",
                }}
              >
                {val}
                <button
                  type="button"
                  onClick={() => onRemove(val)}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    background: "none",
                    border: "none",
                    cursor: "pointer",
                    padding: 0,
                    color: "inherit",
                    opacity: 0.65,
                    lineHeight: 1,
                    fontSize: "12px",
                  }}
                  aria-label={`Quitar ${val}`}
                >
                  ✕
                </button>
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default RutListFilter;
